/**
 * rm-announcement-bar
 *
 * Swaps the server-rendered country name in the announcement bar for the
 * visitor's detected country. Detection uses Shopify's own storefront endpoint
 * (`/browsing_context_suggestions.json`), the same one Shopify Markets uses for
 * market recommendations, so there is no third-party geo lookup.
 *
 * Precedence: a region the visitor picked in the region selector wins ("Europe"), as long
 * as the market context is still that region's stand-in country. Otherwise, if the visitor (or the theme-editor "View as" preview) has switched to a
 * market other than the store's home country, the server-rendered country wins and no
 * geo lookup runs. The lookup only refines the default context.
 *
 * Attributes on <rm-announcement-bar>:
 *   data-context-country  ISO code of the current Shopify Markets context
 *   data-home-country     ISO code of the store's home country (the default context)
 *   data-eligible   comma-separated ISO codes the store ships to (from Shopify Markets)
 *   data-restrict   present when the country must be in data-eligible to be shown
 *   data-fallback   plain text to show instead when the detected country is not eligible
 *
 * Inside the element:
 *   [data-rm-country]  every occurrence of the country name (the marquee clones these)
 *   [data-rm-message]  every message node; those containing a country are replaced by the fallback text
 */

const ENDPOINT = '/browsing_context_suggestions.json?country%5Benabled%5D=true';
const CACHE_KEY = 'rm-detected-country';
// Countries whose names take an article mid-sentence ("across the United States"); keep in sync with the section.
const ARTICLE_COUNTRIES = new Set('US,GB,NL,AE,PH,BS,MV,DO,GM,CF,CD,CG,KM,FK,FO,IM,MP,SB,TC,VG,VI,KY,MH,CK,CC,PN,AX,BQ,PS,IO,TF,UM,HM,GS'.split(','));

class RmAnnouncementBar extends HTMLElement {
  connectedCallback() {
    this.#resolve();
    this.#rotate();
  }

  disconnectedCallback() {
    if (this.#timer) clearInterval(this.#timer);
  }

  #timer = null;

  /** "One message at a time" mode: cross-fade through the messages, unless motion is reduced. */
  #rotate() {
    const stage = this.querySelector('[data-rotate]');
    if (!stage) return;
    // Messages hidden for the current preorder phase (snippets/rm-phase.liquid) are skipped.
    const items = [...stage.querySelectorAll('.rm-announcement__usp')].filter(
      (item) => getComputedStyle(item).display !== 'none'
    );
    if (items.length < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const seconds = Math.max(2, Number(stage.dataset.rotate) || 5);
    let index = 0;
    stage.setAttribute('data-rotating', '');
    items[0].setAttribute('data-active', '');
    this.#timer = setInterval(() => {
      items[index].removeAttribute('data-active');
      index = (index + 1) % items.length;
      items[index].setAttribute('data-active', '');
    }, seconds * 1000);
  }

  async #resolve() {
    const context = (this.dataset.contextCountry || '').toUpperCase();
    const home = (this.dataset.homeCountry || '').toUpperCase();

    // A region picked in the region selector (snippets/rm-region-list.liquid) names itself:
    // "Europe" rather than the stand-in country that region submits.
    const region = this.#pickedRegion();
    if (region && region.code === context) {
      for (const node of this.querySelectorAll('[data-rm-country]')) node.textContent = region.label;
      return;
    }

    if (context && home && context !== home) return;

    const detected = await this.#detectCountry();
    if (!detected?.name || !detected?.handle) return;

    const eligible = (this.dataset.eligible || '')
      .split(',')
      .map((code) => code.trim().toUpperCase())
      .filter(Boolean);
    const restrict = this.hasAttribute('data-restrict');
    const isEligible = !restrict || eligible.includes(detected.handle.toUpperCase());

    if (isEligible) {
      for (const node of this.querySelectorAll('[data-rm-country]')) {
        node.textContent = (ARTICLE_COUNTRIES.has(detected.handle.toUpperCase()) ? 'the ' : '') + detected.name;
      }
      return;
    }

    const fallback = (this.dataset.fallback || '').trim();
    if (!fallback) return;

    // Only messages that name a country are replaced; other USPs stay as written.
    for (const node of this.querySelectorAll('[data-rm-message]')) {
      if (node.querySelector('[data-rm-country]')) node.textContent = fallback;
    }
  }

  /**
   * @returns {{ code: string, label: string } | null}
   */
  #pickedRegion() {
    try {
      const region = JSON.parse(localStorage.getItem('rm-region') || 'null');
      if (!region?.code || !region?.label) return null;
      return { code: String(region.code).toUpperCase(), label: String(region.label) };
    } catch {
      return null;
    }
  }

  /**
   * @returns {Promise<{ handle: string, name: string } | null>}
   */
  async #detectCountry() {
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) return JSON.parse(cached);
    } catch {
      // storage unavailable; fall through to a network lookup
    }

    try {
      const response = await fetch(ENDPOINT, { headers: { Accept: 'application/json' } });
      if (!response.ok) return null;

      const data = await response.json();
      const country = data?.detected_values?.country;
      if (!country?.handle || !country?.name) return null;

      const result = { handle: country.handle, name: country.name };
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(result));
      } catch {
        // ignore quota / private-mode errors
      }
      return result;
    } catch {
      return null;
    }
  }
}

if (!customElements.get('rm-announcement-bar')) {
  customElements.define('rm-announcement-bar', RmAnnouncementBar);
}

// The same behaviour for any other block of copy that carries a [country] token
// (e.g. the preorder promo's selling points). Same data attributes, same markup.
if (!customElements.get('rm-geo-text')) {
  customElements.define('rm-geo-text', class RmGeoText extends RmAnnouncementBar {});
}
